import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { 
  Brain, 
  MessageSquare, 
  Calendar, 
  FileText, 
  Bell, 
  Users,
  ChevronRight,
  X
} from 'lucide-react';

interface OnboardingStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  image: string;
}

export default function OnboardingPage() {
  useDocumentTitle('Welcome to CampusConnect');
  
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const steps: OnboardingStep[] = [
    {
      icon: <Brain className="w-12 h-12 text-primary-500" />,
      title: "AI-Powered Dashboard",
      description: "Your personalized command center with smart notifications, task prioritization, and automated workflows tailored to your role.",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80&w=2070"
    },
    {
      icon: <MessageSquare className="w-12 h-12 text-secondary-500" />,
      title: "Smart Communication",
      description: "Stay connected with AI-enhanced messaging, automated summaries of long conversations, and role-based channels.",
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=2070"
    },
    {
      icon: <Calendar className="w-12 h-12 text-accent-500" />,
      title: "Unified Calendar",
      description: "One calendar for everything - meetings, events, deadlines, and automated reminders to keep everyone in sync.",
      image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&q=80&w=2068"
    },
    {
      icon: <FileText className="w-12 h-12 text-secondary-500" />,
      title: "Document Hub",
      description: "Access all your important documents in one place with smart organization and quick search capabilities.",
      image: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?auto=format&fit=crop&q=80&w=2032"
    },
    {
      icon: <Bell className="w-12 h-12 text-accent-500" />,
      title: "Smart Notifications",
      description: "Never miss important updates with AI-prioritized notifications and automated reminders for critical tasks.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=2070"
    },
    {
      icon: <Users className="w-12 h-12 text-primary-500" />,
      title: "Team Collaboration",
      description: "Work together seamlessly with department channels, shared resources, and automated task distribution.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2070"
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Skip button */}
      <button
        onClick={handleSkip}
        className="fixed top-4 right-4 p-2 text-text-secondary hover:text-text-primary 
                 transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="container mx-auto px-4 py-8 min-h-screen flex flex-col">
        {/* Progress bar */}
        <div className="w-full max-w-3xl mx-auto mb-8">
          <div className="h-1 bg-border-light rounded-full">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center max-w-4xl mx-auto">
          <div className="w-full bg-surface-light rounded-2xl shadow-xl overflow-hidden">
            {/* Image */}
            <div className="relative h-64 md:h-80">
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <img
                src={steps[currentStep].image}
                alt={steps[currentStep].title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="flex flex-col items-center text-center">
                <div className="p-3 bg-surface-dark rounded-2xl mb-6">
                  {steps[currentStep].icon}
                </div>
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  {steps[currentStep].title}
                </h2>
                <p className="text-lg text-text-secondary mb-8 max-w-2xl">
                  {steps[currentStep].description}
                </p>
              </div>

              {/* Navigation */}
              <div className="flex justify-center">
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg 
                           flex items-center gap-2 transition-colors"
                >
                  {currentStep === steps.length - 1 ? "Get Started" : "Next"}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep ? 'bg-primary-500' : 'bg-border-light'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}